import React, { useEffect } from "react"
import { 
  Chart as ChartJS, 
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  DoughnutController,
  BarController,
  ChartTypeRegistry,
  ChartOptions
} from 'chart.js'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  DoughnutController,
  BarController
)

// Set global defaults for all charts
ChartJS.defaults.font.size = 12;
ChartJS.defaults.font.family = "'Poppins', 'Helvetica', sans-serif";
ChartJS.defaults.plugins.legend.labels.font = {
  size: 12,
  family: "'Poppins', 'Helvetica', sans-serif"
};

export interface ChartConfig<T extends keyof ChartTypeRegistry> {
  type: T;
  data: {
    labels: string[];
    datasets: {
      label?: string;
      data: number[];
      backgroundColor?: string | string[];
      borderColor?: string | string[];
      borderWidth?: number;
    }[];
  };
  options?: ChartOptions<T>;
  ref: React.RefObject<HTMLCanvasElement>;
}

interface ReportChartsProps {
  charts: ChartConfig<keyof ChartTypeRegistry>[];
}

/**
 * A reusable component for rendering multiple charts in reports
 * Can be configured with different chart types, data, and options
 */
const ReportCharts: React.FC<ReportChartsProps> = ({ charts }) => {
  useEffect(() => {
    // Only run in browser environment
    if (typeof window === "undefined") return

    // Clean up any existing charts
    const cleanup = () => {
      charts.forEach(chart => {
        if (chart.ref.current) {
          const chartInstance = ChartJS.getChart(chart.ref.current)
          if (chartInstance) {
            chartInstance.destroy()
          }
        }
      })
    }
    
    // Clean up first
    cleanup()
    
    // Initialize charts
    charts.forEach(chart => {
      if (chart.ref.current) {
        new ChartJS(chart.ref.current.getContext('2d')!, {
          type: chart.type,
          data: chart.data,
          options: chart.options
        })
      }
    })
    
    // Cleanup on unmount
    return cleanup
  }, [charts])

  return null
}

export default ReportCharts